// [T155P3] Account Freshness Health sub-signal
// Shows accounts with updatedAt older than 30 days.
// Source: Redux state.accounts.accounts (already loaded on Dashboard).
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import type { Account } from '@/lib/types';

type StatusLevel = 'green' | 'amber' | 'red';

const statusTextClass: Record<StatusLevel, string> = {
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

interface Props {
  accounts: Account[];
  status: StatusLevel;
}

export default function AccountFreshnessHealth({ accounts, status }: Props) {
  const navigate = useNavigate();

  if (accounts.length === 0) {
    return (
      <div data-testid="account-freshness-health" className="space-y-1">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Account Freshness</span>
        </div>
        <p className="text-sm text-green-600 dark:text-green-400">No accounts</p>
      </div>
    );
  }

  const now = Date.now();
  const staleAccounts = accounts.filter(
    (a) => now - new Date(a.updatedAt).getTime() > THIRTY_DAYS_MS
  );

  return (
    <div data-testid="account-freshness-health" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Account Freshness</span>
        </div>
        {staleAccounts.length === 0 ? (
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            All fresh
          </span>
        ) : (
          <span className={`text-sm font-semibold ${statusTextClass[status]}`}>
            {staleAccounts.length} stale
          </span>
        )}
      </div>
      {staleAccounts.length > 0 && (
        <div className="space-y-1">
          {staleAccounts.slice(0, 3).map((a) => (
            <div key={a.id} className="flex items-center gap-2">
              <span
                className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                  status === 'red' ? 'bg-red-500' : 'bg-amber-500'
                }`}
              />
              <span className="text-xs text-muted-foreground break-words">{a.name}</span>
            </div>
          ))}
          {staleAccounts.length > 3 && (
            <p className="pl-3.5 text-xs text-muted-foreground">
              +{staleAccounts.length - 3} more
            </p>
          )}
          <button
            onClick={() => navigate('/accounts')}
            className="min-h-[44px] flex items-center text-xs text-primary underline-offset-2 hover:underline"
          >
            Update balances →
          </button>
        </div>
      )}
    </div>
  );
}
